import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import './index.css';
import WaveLogo from './Logo/Wave';
import { colors, profitTone } from '../../../colors';
import { selectIsDev, selectUserId } from '../../../store/slices/user';
import { enterDemo, exitDemo, selectIsDemo } from '../../../store/slices/demo';
import { useLogoutMutation } from '../../../store/services/api';
import {
  ChevronDownIcon,
  OverviewIcon,
  DiscoverIcon,
  SubscriptionsIcon,
  BalanceIcon,
  SettingsIcon,
} from './icons';
import { useStretchRail } from './useStretchRail';
import ProfileDropdown, {
  Credits,
  Exchange,
  Identity,
} from './ProfileDropdown';
import SettingsDropdown from './SettingsDropdown';

// EXPERIMENT: show an icon in front of each primary tab label.
// Set to false to revert to text-only tabs.
const SHOW_TAB_ICONS = true;

// Primary tabs — visible in the bar at every breakpoint.
const TABS = [
  { label: 'Overview', href: '/', Icon: OverviewIcon },
  { label: 'Discover', href: '/discover', Icon: DiscoverIcon },
  { label: 'Subscriptions', href: '/sub', Icon: SubscriptionsIcon },
  { label: 'Balance', href: '/balance', Icon: BalanceIcon },
];

// DUMMY DATA — replace with fetched values later (credits, exchanges, identity).
const DUMMY_IDENTITY: Identity = {
  name: 'Wouter Van Hecke',
  email: 'wouter@automagically.be',
  initial: 'W',
};
const DEMO_IDENTITY: Identity = {
  name: 'Demo Account',
  email: '',
  initial: 'D',
};
const DUMMY_CREDITS: Credits = { used: 50, total: 50 };
const DUMMY_EXCHANGES: Exchange[] = [
  { name: 'Binance', status: 'ok' },
  { name: 'KuCoin', status: 'ok' },
  { name: 'MEXC', status: 'ok' },
  { name: 'Coinbase', status: 'ok' },
];

// Map the current route to a primary-tab index. -1 hides the rail
// (operator pages, account, bot detail, etc. have no in-bar tab).
const getActiveIndex = (pathname: string): number => {
  if (pathname.includes('/discover')) return 1;
  if (pathname.includes('/sub')) return 2;
  if (pathname.includes('/balance')) return 3;
  const railless = [
    '/control',
    '/account',
    '/profitCalculations',
    '/detail',
    '/presentation',
    '/privacy',
    '/bot',
  ];
  if (railless.some((p) => pathname.includes(p))) return -1;
  return 0; // Overview / root
};

interface Props {
  hasPositiveProfit?: boolean;
}

const TopBar = ({ hasPositiveProfit }: Props) => {
  const [logout] = useLogoutMutation();
  const location = useLocation();
  const dispatch = useDispatch();
  const userId = useSelector(selectUserId);
  const isDev = useSelector(selectIsDev);
  const isDemo = useSelector(selectIsDemo);

  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tabSheetOpen, setTabSheetOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);

  // In demo mode only Overview + Subscriptions are reachable; Discover and
  // Balance are hidden from the primary tabs as well as from the routing.
  const visibleTabs = useMemo(
    () =>
      isDemo ? TABS.filter((t) => t.href === '/' || t.href === '/sub') : TABS,
    [isDemo],
  );

  // Active index is positional within `visibleTabs` — getActiveIndex returns
  // an index in the full TABS array, which gets shifted when tabs are hidden
  // (e.g. /sub is index 2 in TABS but index 1 once Discover is filtered out
  // for demo). Translate via href lookup so the rail and is-active class land
  // on the right tab regardless of which tabs are visible.
  const fullActiveIdx = useMemo(
    () => getActiveIndex(location.pathname),
    [location.pathname],
  );
  const activeIdx = useMemo(() => {
    if (fullActiveIdx < 0 || fullActiveIdx >= TABS.length) return fullActiveIdx;
    const href = TABS[fullActiveIdx].href;
    return visibleTabs.findIndex((t) => t.href === href);
  }, [fullActiveIdx, visibleTabs]);
  const { rail, railRef, tabRefs } = useStretchRail(activeIdx);

  const tone =
    hasPositiveProfit === undefined
      ? profitTone.neutral
      : hasPositiveProfit
      ? profitTone.up
      : profitTone.down;

  const backgroundGradientColor = useMemo(() => {
    if (typeof hasPositiveProfit === 'undefined') {
      return colors.black;
    }
    return hasPositiveProfit
      ? 'linear-gradient(to top, rgba(0, 255, 38, 0.19) -70% , black 80%)'
      : 'linear-gradient(to top, rgba(255, 0, 0, 0.19) -70% , black 80%)';
  }, [hasPositiveProfit]);

  // Close overlays on navigation.
  useEffect(() => {
    setMenuOpen(false);
    setSettingsOpen(false);
    setTabSheetOpen(false);
  }, [location.pathname]);

  // Close overlays on outside-click / Escape.
  useEffect(() => {
    if (!menuOpen && !settingsOpen && !tabSheetOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setSettingsOpen(false);
        setTabSheetOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setSettingsOpen(false);
        setTabSheetOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, settingsOpen, tabSheetOpen]);

  const handleLogout = useCallback(() => {
    setMenuOpen(false);
    logout({ userId });
  }, [logout, userId]);

  const handleEnterDemo = useCallback(() => {
    setMenuOpen(false);
    dispatch(enterDemo());
  }, [dispatch]);

  const handleExitDemo = useCallback(() => {
    setMenuOpen(false);
    dispatch(exitDemo());
  }, [dispatch]);

  return (
    <div className="apex-root" ref={rootRef}>
      <div
        className="apex-bar"
        style={{
          backgroundImage: backgroundGradientColor,
          borderBottom: `2px solid ${tone.line}`,
        }}
      >
        {/* Mobile-only hamburger — animated morph to an X when open */}
        <button
          type="button"
          className="apex-icon-btn apex-hamburger"
          aria-label={tabSheetOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={tabSheetOpen}
          onClick={() => {
            setTabSheetOpen((o) => !o);
            setMenuOpen(false);
          }}
        >
          <span className={'apex-burger' + (tabSheetOpen ? ' is-open' : '')}>
            <span />
            <span />
            <span />
          </span>
        </button>

        {/* Logo — crossfades between AutoMagically and GRTS */}
        <Link to="/" className="apex-logo" aria-label="Home">
          <span className="apex-logo-box">
            <WaveLogo
              hasPositiveProfit={hasPositiveProfit}
              label="AutoMagically"
              first
            />
            <WaveLogo
              hasPositiveProfit={hasPositiveProfit}
              label="GRTS"
              first={false}
            />
          </span>
        </Link>

        {/* Primary tabs + animated rail */}
        <nav className="apex-nav">
          {visibleTabs.map((tab, i) => (
            <Link
              key={tab.href}
              to={tab.href}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              className={'apex-tab' + (i === activeIdx ? ' is-active' : '')}
            >
              {SHOW_TAB_ICONS && (
                <span className="apex-tab-icon">
                  <tab.Icon size={17} />
                </span>
              )}
              {tab.label}
            </Link>
          ))}
          <span
            ref={railRef}
            className="apex-rail"
            style={{
              left: rail.left,
              width: rail.width,
              color: tone.rail,
              boxShadow: `0 0 10px ${tone.glow}, 0 0 4px ${tone.glow}`,
              opacity: rail.width > 0 ? 1 : 0,
            }}
          />
        </nav>

        {/* Demo settings — gear button just to the left of the profile avatar */}
        {isDemo && (
          <div className="apex-profile" style={{ marginRight: -4 }}>
            <button
              type="button"
              className={'apex-avatar-btn' + (settingsOpen ? ' is-open' : '')}
              aria-haspopup="menu"
              aria-expanded={settingsOpen}
              aria-label="Demo settings"
              onClick={() => {
                setSettingsOpen((o) => !o);
                setMenuOpen(false);
                setTabSheetOpen(false);
              }}
            >
              <span
                className="apex-avatar"
                style={{ background: 'transparent', color: '#e2e2e2' }}
              >
                <SettingsIcon size={18} />
              </span>
            </button>
            {settingsOpen && <SettingsDropdown />}
          </div>
        )}

        {/* Profile cluster */}
        <div className="apex-profile">
          <button
            type="button"
            className={'apex-avatar-btn' + (menuOpen ? ' is-open' : '')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Account menu"
            onClick={() => {
              setMenuOpen((o) => !o);
              setSettingsOpen(false);
              setTabSheetOpen(false);
            }}
          >
            <span className="apex-avatar">
              {(isDemo ? DEMO_IDENTITY : DUMMY_IDENTITY).initial}
            </span>
            <ChevronDownIcon size={16} className="apex-avatar-chev" />
          </button>

          {menuOpen && (
            <ProfileDropdown
              isDev={isDev}
              isDemo={isDemo}
              wasLoggedIn={isDemo && userId != null}
              identity={isDemo ? DEMO_IDENTITY : DUMMY_IDENTITY}
              credits={DUMMY_CREDITS}
              exchanges={DUMMY_EXCHANGES}
              onLogout={handleLogout}
              onEnterDemo={handleEnterDemo}
              onExitDemo={handleExitDemo}
            />
          )}
        </div>
      </div>

      {/* Mobile tab sheet */}
      {tabSheetOpen && (
        <div className="apex-mobile-sheet">
          {visibleTabs.map((tab, i) => (
            <Link
              key={tab.href}
              to={tab.href}
              className={
                'apex-mobile-row' + (i === activeIdx ? ' is-active' : '')
              }
            >
              {i === activeIdx && (
                <span
                  className="apex-mobile-dot"
                  style={{ background: tone.rail }}
                />
              )}
              <span style={{ marginLeft: i === activeIdx ? 0 : 18 }}>
                {tab.label}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopBar;
